import React, { useState } from "react"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import { Box, List, ListItem, ListItemText, Typography, Divider } from "@mui/material"
import { AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppNote, AppValueChip } from "@components"
import { dsColorNew } from "@palette"
import {useUploadCsv} from "./@hooks";
import { secondaryButtonSx } from "@components"

function formatErrorData(data) {
    if (!data) return null
    if (typeof data === 'string') return data
    if (typeof data === 'object') {
        const relevantKeys = ['MODEL', 'CATEGORY', 'ACRISS', 'OFFICE COUNTRY', 'OFFICE NAME', 'PROVIDERS', 'WEB', 'DOORS', 'SEATS']
        return Object.entries(data)
            .filter(([key]) => relevantKeys.includes(key.toUpperCase()))
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
    }
    return String(data)
}

function formatErrorMessage(error) {
    if (!error) return ''
    if (error.includes('validation error')) {
        const match = error.match(/Input should be ([^[]+)/g)
        if (match) return match.join('; ')
    }
    return error
}

function ValidationPreviewDialog({ open, onClose, onConfirm, result, isLoading }) {
    if (!result) return null

    const { valid_items = [], valid_count = 0, update_items = [], update_count = 0, failed = 0, total_rows = 0, errors = [], skipped = [], skipped_count = 0, warnings = [], warning_count = 0 } = result
    const hasValidItems = valid_count > 0 || update_count > 0

    return (
        <AppDialog open={open} onClose={onClose} maxWidth="md">
            <AppDialogHeader icon="file-xls" title="XLSX Validation Preview" subtitle={`${total_rows} rows read`} onClose={onClose}/>
            <AppDialogBody>
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <AppValueChip>{`Total rows: ${total_rows}`}</AppValueChip>
                    {valid_count > 0 && <AppValueChip sx={{ backgroundColor: dsColorNew.successBg, color: dsColorNew.successText }}>{`New: ${valid_count}`}</AppValueChip>}
                    {update_count > 0 && <AppValueChip tone="accent">{`Updates: ${update_count}`}</AppValueChip>}
                    {skipped_count > 0 && <AppValueChip tone="warning">{`Skipped: ${skipped_count}`}</AppValueChip>}
                    {warning_count > 0 && <AppValueChip tone="warning">{`Warnings: ${warning_count}`}</AppValueChip>}
                    {failed > 0 && <AppValueChip sx={{ backgroundColor: dsColorNew.dangerBg, color: dsColorNew.dangerRed }}>{`Errors: ${failed}`}</AppValueChip>}
                </Box>

                {hasValidItems && failed === 0 && skipped_count === 0 && warning_count === 0 && (
                    <AppNote severity="success" sx={{ mb: 2 }}>
                        {valid_count > 0 && update_count > 0
                            ? `${valid_count} new rows and ${update_count} updates are ready to be applied.`
                            : valid_count > 0
                                ? `All ${valid_count} rows are new and ready to be applied.`
                                : `${update_count} existing records will be updated.`
                        }
                    </AppNote>
                )}

                {hasValidItems && (failed > 0 || skipped_count > 0 || warning_count > 0) && (
                    <AppNote severity="warning" sx={{ mb: 2 }}>
                        {valid_count > 0 && `${valid_count} new rows`}
                        {valid_count > 0 && update_count > 0 && ' and '}
                        {update_count > 0 && `${update_count} updates`}
                        {' can be applied. '}
                        {failed > 0 && `${failed} rows have errors. `}
                        {skipped_count > 0 && `${skipped_count} duplicate rows will be skipped. `}
                        {warning_count > 0 && `${warning_count} rows have ignored values (not found).`}
                    </AppNote>
                )}

                {!hasValidItems && failed > 0 && (
                    <AppNote severity="error" sx={{ mb: 2 }}>
                        No valid rows found. All {failed} rows have errors.
                    </AppNote>
                )}

                {valid_items.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: dsColorNew.successText }}>
                            New items to be added ({valid_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: dsColorNew.successBg, borderRadius: '10px', border: `1px solid ${dsColorNew.successBar}` }}>
                            {valid_items.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{item.display}</Typography>}
                                    />
                                </ListItem>
                            ))}
                            {valid_items.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>... and {valid_items.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {update_items.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: dsColorNew.appBlue }}>
                            Existing items to be updated ({update_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: dsColorNew.noteInfoBg, borderRadius: '10px', border: `1px solid ${dsColorNew.noteInfoBorder}` }}>
                            {update_items.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{item.display}</Typography>}
                                        secondary={
                                            <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
                                                {item.old_value} → {item.new_value}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {update_items.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>... and {update_items.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {skipped.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: dsColorNew.noteAmber }}>
                            Skipped ({skipped_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: dsColorNew.noteAmberBg, borderRadius: '10px', border: `1px solid ${dsColorNew.noteAmberBorder}` }}>
                            {skipped.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {item.reason === 'duplicate' ? (
                                                    <>
                                                        <AppValueChip tone="warning">{`Row ${item.row}`}</AppValueChip>
                                                        <Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>
                                                            Duplicate of row {item.original_row}
                                                        </Typography>
                                                    </>
                                                ) : item.reason === 'no_change' ? (
                                                    <>
                                                        <AppValueChip>No change</AppValueChip>
                                                        <Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>
                                                            Already exists with same value: {item.value}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{item.reason}</Typography>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, wordBreak: 'break-word' }}>
                                                {item.data}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {skipped.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>... and {skipped.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {warnings.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: dsColorNew.noteAmber }}>
                            Warnings ({warning_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: dsColorNew.noteAmberBg, borderRadius: '10px', border: `1px solid ${dsColorNew.noteAmberBorder}` }}>
                            {warnings.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <AppValueChip tone="warning">{item.field}</AppValueChip>
                                                <Typography sx={{ fontSize: 13, color: dsColorNew.textDark, wordBreak: 'break-word' }}>
                                                    {item.message}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, wordBreak: 'break-word' }}>
                                                {item.data}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {warnings.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>... and {warnings.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {errors.length > 0 && (
                    <Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: dsColorNew.dangerRed }}>
                            Errors ({errors.length}):
                        </Typography>
                        <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: dsColorNew.tableRowAlt, borderRadius: '10px', border: `1px solid ${dsColorNew.grayBorder}` }}>
                            {errors.slice(0, 30).map((err, idx) => (
                                <ListItem key={idx} divider>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {err.row && <AppValueChip>{`Row ${err.row}`}</AppValueChip>}
                                                <AppValueChip sx={{ backgroundColor: dsColorNew.dangerBg, color: dsColorNew.dangerRed }}>{err.field}</AppValueChip>
                                                <Typography sx={{ fontSize: 13, color: dsColorNew.textDark, wordBreak: 'break-word' }}>
                                                    {formatErrorMessage(err.error)}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            formatErrorData(err.data) && (
                                                <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, wordBreak: 'break-word' }}>
                                                    {formatErrorData(err.data)}
                                                </Typography>
                                            )
                                        }
                                    />
                                </ListItem>
                            ))}
                            {errors.length > 30 && (
                                <ListItem>
                                    <ListItemText primary={`... and ${errors.length - 30} more errors`} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}
            </AppDialogBody>
            <AppDialogFooter>
                <AppDialogCancel onClick={onClose}/>
                {hasValidItems && (
                    <AppBulkEditAction
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? "Applying..." : `Apply ${valid_count + update_count} items`}
                    </AppBulkEditAction>
                )}
            </AppDialogFooter>
        </AppDialog>
    )
}

export default function UploadXlsButton({ path }) {
    const { fileRef, validateFile, applyChanges, isLoadingValidate, isLoadingApply, validationResult, clearResult } = useUploadCsv(path)
    const [dialogOpen, setDialogOpen] = useState(false)

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            validateFile(file)
        }
    }

    React.useEffect(() => {
        if (validationResult.value) {
            setDialogOpen(true)
        }
    }, [validationResult.value])

    const handleConfirm = () => {
        applyChanges()
        setDialogOpen(false)
    }

    const handleClose = () => {
        setDialogOpen(false)
        clearResult()
        if (fileRef.current) {
            fileRef.current.value = ''
        }
    }

    const isLoading = isLoadingValidate.value || isLoadingApply.value

    return (
        <>
            <ButtonLoadingSeeker
                variant="outlined"
                sx={secondaryButtonSx}
                startIcon={<IconSeeker icon="file-xls"/>}
                loading={isLoading}
                component="label"
            >
                <Box display={'none'}>
                    <input
                        type="file"
                        ref={fileRef}
                        accept=".xlsx"
                        onChange={handleFileChange}
                    />
                </Box>
                {isLoadingValidate.value ? "Validating..." : isLoadingApply.value ? "Applying..." : "Upload XLSX"}
            </ButtonLoadingSeeker>

            <ValidationPreviewDialog
                open={dialogOpen}
                onClose={handleClose}
                onConfirm={handleConfirm}
                result={validationResult.value}
                isLoading={isLoadingApply.value}
            />
        </>
    )
}